<div class="page inbox">
  <h1 class="c-h1">{{ 'MENU.inbox' | translate }}</h1>

  @if (state$ | async; as state) {
    <!-- Filter bar -->
    @if (!state.loading && hasData()) {
      <div class="inbox-filter" role="tablist" [attr.aria-label]="'INBOX.filter_aria_label' | translate">
        <button
          type="button"
          role="tab"
          class="inbox-filter__tab"
          [class.inbox-filter__tab--active]="activeFilter() === 'all'"
          [attr.aria-selected]="activeFilter() === 'all'"
          (click)="onFilterChange('all')">
          <span>{{ 'INBOX.filter_all' | translate }}</span>
          <span class="inbox-filter__count">{{ counts().all }}</span>
        </button>

        <button
          type="button"
          role="tab"
          class="inbox-filter__tab"
          [class.inbox-filter__tab--active]="activeFilter() === 'unread'"
          [attr.aria-selected]="activeFilter() === 'unread'"
          (click)="onFilterChange('unread')">
          <span>{{ 'INBOX.filter_unread' | translate }}</span>
          <span class="inbox-filter__count">{{ counts().unread }}</span>
        </button>

        <button
          type="button"
          role="tab"
          class="inbox-filter__tab"
          [class.inbox-filter__tab--active]="activeFilter() === 'read'"
          [attr.aria-selected]="activeFilter() === 'read'"
          (click)="onFilterChange('read')">
          <span>{{ 'INBOX.filter_read' | translate }}</span>
          <span class="inbox-filter__count">{{ counts().read }}</span>
        </button>

        <button
          type="button"
          role="tab"
          class="inbox-filter__tab"
          [class.inbox-filter__tab--active]="activeFilter() === 'to-sign'"
          [attr.aria-selected]="activeFilter() === 'to-sign'"
          (click)="onFilterChange('to-sign')">
          <i class="fa fa-pen"></i>
          <span>{{ 'INBOX.filter_to_sign' | translate }}</span>
          <span class="inbox-filter__count">{{ counts().toSign }}</span>
        </button>
      </div>
    }

    <myabvv-alert [condition]="state.error" [message]="'USER_FEEDBACK.error_occurred'" [type]="'error'"></myabvv-alert>

    @if (state.loading) {
      <myabvv-loader [message]="'GENERAL.busy_loading' | translate"></myabvv-loader>
    }

    <!-- Sort toolbar: icon-only toggle (newest first / oldest first) -->
    @if (!state.loading && hasData() && filteredMessages().length > 0) {
      <div class="inbox-sort" role="group" [attr.aria-label]="'INBOX.sort_aria_label' | translate">
        <button
          type="button"
          class="inbox-sort__btn"
          [class.inbox-sort__btn--active]="sortDirection() === 'desc'"
          [attr.aria-pressed]="sortDirection() === 'desc'"
          [attr.aria-label]="'INBOX.sort_newest_first' | translate"
          [title]="'INBOX.sort_newest_first' | translate"
          (click)="onSortChange('desc')">
          <i class="fa fa-sort-amount-down"></i>
        </button>
        <button
          type="button"
          class="inbox-sort__btn"
          [class.inbox-sort__btn--active]="sortDirection() === 'asc'"
          [attr.aria-pressed]="sortDirection() === 'asc'"
          [attr.aria-label]="'INBOX.sort_oldest_first' | translate"
          [title]="'INBOX.sort_oldest_first' | translate"
          (click)="onSortChange('asc')">
          <i class="fa fa-sort-amount-up-alt"></i>
        </button>
      </div>

      <div class="inbox-messages">
        @for (message of pagedMessages(); track message.id) {
          <div class="inbox-message" [class.inbox-message--unread]="message?.status === inboxMessageStatus.UNREAD">
            <!-- Unread indicator -->
            @if (message?.status === inboxMessageStatus.UNREAD) {
              <div class="inbox-message__unread-indicator"></div>
            }

            <div class="inbox-message__content">
              <!-- Header row: date and status -->
              <div class="inbox-message__header">
                <div class="inbox-message__date">
                  <i class="fa fa-calendar-alt"></i>
                  {{ message?.datePosted | date:'dd/MM/yyyy' }}
                </div>
                <div class="inbox-message__status">
                  @if (message?.status === inboxMessageStatus.UNREAD) {
                    <span class="inbox-status inbox-status--unread">
                      <i class="fa fa-envelope"></i>
                      {{ 'INBOX.status_unread' | translate }}
                    </span>
                  } @else {
                    <span class="inbox-status inbox-status--read">
                      <i class="fa fa-envelope-open"></i>
                      {{ 'INBOX.status_read' | translate }}
                    </span>
                  }
                </div>
              </div>

              <!-- Description -->
              <div class="inbox-message__description">
                {{ message?.description }}
              </div>

              <!-- Comment -->
              @if (message?.comment) {
                <div class="inbox-message__comment">
                  <i class="fa fa-comment"></i>
                  {{ message?.comment }}
                </div>
              }

              <!-- Attachments and Actions -->
              @if (message.attachments?.length > 0) {
                <div class="inbox-message__attachments">
                  @for (inboxAttachment of message.attachments; track inboxAttachment) {
                    <div class="inbox-attachment">
                      <!-- Download button -->
                      <button class="inbox-attachment__download"
                              [class.is-disabled]="isDownloading()"
                              (click)="!isDownloading() && downloadFile(inboxAttachment)">
                        <i class="fa fa-file-pdf"></i>
                        <span>{{ 'INBOX.download_pdf' | translate }}</span>
                      </button>

                      <!-- Signature status -->
                      @if (isSignableDocument(inboxAttachment)) {
                        <div class="inbox-attachment__signature">
                          @if (needSignature(inboxAttachment)) {
                            <button class="inbox-signature-btn"
                                    (click)="onSubmitSignForm(inboxAttachment, message.description)"
                                    title="{{ 'INBOX.sign_this_document' | translate }}">
                              <i class="fa fa-pen"></i>
                              {{ 'INBOX.sign_document' | translate }}
                            </button>
                          }
                          @if (displayStatus(inboxAttachment) === signatureStatus.PENDING) {
                            <span class="inbox-signature-status inbox-signature-status--pending">
                              <i class="fa fa-clock"></i>
                              {{ ('INBOX.signature_' + displayStatus(inboxAttachment).toLowerCase()) | translate }}
                            </span>
                          }
                          @if (displayStatus(inboxAttachment) === signatureStatus.SIGNED) {
                            <span class="inbox-signature-status inbox-signature-status--signed">
                              <i class="fa fa-check-circle"></i>
                              {{ ('INBOX.signature_' + displayStatus(inboxAttachment).toLowerCase()) | translate }}
                            </span>
                          }
                          @if (displayStatus(inboxAttachment) === signatureStatus.ERROR) {
                            <span class="inbox-signature-status inbox-signature-status--error">
                              <i class="fa fa-exclamation-circle"></i>
                              {{ ('INBOX.signature_' + displayStatus(inboxAttachment).toLowerCase()) | translate }}
                            </span>
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>

      <myabvv-pagination [pages]="pages()" [activePage]="currentPage()" (selectPageEvent)="onPageChange($event)"></myabvv-pagination>
    }

    <!-- Filtered empty state: inbox has messages but none match the active filter -->
    @if (!state.loading && !state.error && hasData() && filteredMessages().length === 0) {
      <div class="info-card-empty info-card-empty--stacked">
        <div class="info-card-empty__row">
          <i class="fa fa-filter info-card-empty__icon"></i>
          <span>{{ emptyFilterMessageKey() | translate }}</span>
        </div>
        <button type="button"
                class="inbox-filter__reset"
                (click)="onFilterChange('all')">
          {{ 'INBOX.filter_show_all' | translate }}
        </button>
      </div>
    }

    <!-- Inbox is truly empty (no messages at all) -->
    @if (!hasData() && !state.loading && !state.error) {
      <div class="info-card-empty">
        <i class="fa fa-inbox info-card-empty__icon"></i>
        <span>{{ 'INBOX.no_messages' | translate }}</span>
      </div>
    }
  }
</div>
